import { Component, inject } from '@angular/core';
import { CommonModule, PlatformLocation } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ActionItemComponent } from '../../shared/action-item.component';
import { GroupsService } from '../groups.service';
import { UpdateStudentDto } from '@oracychampions/api-interfaces';

@Component({
  selector: 'oc-edit-students',
  standalone: true,
  imports: [CommonModule, RouterModule, ActionItemComponent, ReactiveFormsModule, FormlyBootstrapModule, FormlyModule],
  template: `
    <div class="modal-container">
      <div class="modal-spacer"></div>
      <div class="modal-body">
        <h1 *ngIf="!id">Add Student</h1>
        <h1 *ngIf="id">Edit Student</h1>
        <form [formGroup]="form" (ngSubmit)="onSubmit(model)">
          <formly-form [form]="form" [fields]="fields" [model]="model" [options]="options"></formly-form>
          <div class="d-flex gap-2">
            <button type="submit" class="btn btn-primary mt-3 mb-4">
              <span *ngIf="saving" class="spinner-border spinner-border-sm"></span>
              <span *ngIf="!saving">Save Changes</span>
            </button>
            <button type="button" class="btn btn-secondary mt-3 mb-4" (click)="activeModal.dismiss('Cancel')">
              Cancel
            </button>
          </div>
        </form>
      </div>
      <div class="modal-spacer">
        <oc-action-item icon="tablerX" size="lg" (click)="activeModal.dismiss('Cross click')"></oc-action-item>
      </div>
    </div>
  `,
  styles: `
    .modal-container {
      display: flex;
      min-height: 100vh;
    }

    .modal-body {
      flex: 0;
      flex-basis: 560px;
      padding: 2.5rem 3.5rem;
    }

    .modal-spacer {
      flex: 1; 
    }

    .modal-spacer:last-child {
      padding: 2rem;
      display: flex;
      justify-content: flex-end;
      align-items: flex-start;
    }

    h1 {
      margin-bottom: 2rem;
    }
  `,
})
export class EditStudentsComponent {
  private platformLocation = inject(PlatformLocation);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  private groupsService = inject(GroupsService);
  public activeModal = inject(NgbActiveModal);

  public id: string | null = null;
  public groupId: string | null = null;

  saving = false;
  options: FormlyFormOptions = {
    formState: {
      submitted: false,
    },
  };

  form = new FormGroup({});
  model = { name: '' };
  fields: FormlyFieldConfig[] = [
    {
      key: 'name',
      type: 'input',
      props: {
        label: 'Name',
        required: true,
      },
    },
  ];

  constructor() {
    this.platformLocation.onPopState(() => this.modalService.dismissAll());
  }

  onSubmit(model: any) {
    this.options.formState.submitted = true;
    if (this.saving) {
      return;
    }
    if (!this.form.valid) {
      this.toastr.error('Please review and try again');
      return;
    }

    this.saving = true;

    const student: UpdateStudentDto = {
      ...model,
      groupId: this.groupId,
    };

    if (this.id) {
      this.groupsService.updateStudent(this.id, student).subscribe((response) => {
        this.toastr.success('Student updated');
        this.activeModal.close(response);
      });
    } else {
      this.groupsService.createStudent(student).subscribe((response) => {
        this.toastr.success('Student created');
        this.activeModal.close(response);
      });
    }
  }
}
